import React, { useEffect } from 'react';
import { motion } from 'motion/react';
import { Calendar, User, ArrowLeft, ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const fadeUp = {
  hidden: { opacity: 0, y: 30, transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] } },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.25, 1, 0.5, 1] } }
};

export default function AIAgents() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="pt-32 pb-24 min-h-screen bg-black text-white relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-purple-900/20 rounded-full blur-[120px] pointer-events-none z-0" />
      
      <div className="max-w-4xl w-full mx-auto px-4 sm:px-8 relative z-10">
        
        <motion.div variants={fadeUp} initial="hidden" animate="visible" className="mb-8">
          <Link to="/blogs" className="inline-flex items-center text-sm font-body text-white/50 hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Blogs
          </Link>
        </motion.div>

        <motion.div variants={fadeUp} initial="hidden" animate="visible" className="mb-12">
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <span className="px-3 py-1 text-xs font-medium rounded-full bg-white/10 text-white font-body border border-white/5">
              AI Agents
            </span>
            <div className="flex items-center gap-1.5 text-xs text-white/40 font-body">
              <Calendar className="w-3.5 h-3.5" />
              May 01, 2026
            </div>
            <div className="flex items-center gap-1.5 text-xs text-white/40 font-body">
              <User className="w-3.5 h-3.5" />
              EsyAI Team
            </div>
          </div>
          
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading text-white leading-[1.1] mb-6">
            What Are AI Agents and How Can They Help Your Business?
          </h1>
          
          <p className="text-xl text-white/60 font-body font-light leading-relaxed">
            Discover what AI agents are, how they work, and how UK businesses are using them to automate complex tasks, boost productivity, and scale faster than ever.
          </p>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.25, 1, 0.5, 1] }}
          className="w-full aspect-[16/9] md:aspect-[21/9] rounded-3xl overflow-hidden mb-16 border border-white/10 liquid-glass shadow-2xl relative"
        >
          <img 
            src="/blogs/blog-5-new.png" 
            alt="AI Agents Collaboration" 
            className="w-full h-full object-cover opacity-80"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent pointer-events-none" />
        </motion.div>

        <motion.article 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4, ease: [0.25, 1, 0.5, 1] }}
          className="prose prose-invert prose-lg max-w-none font-body text-white/70 prose-headings:font-heading prose-headings:text-white prose-a:text-purple-400 hover:prose-a:text-purple-300 prose-p:leading-loose"
        >
          <p className="lead text-xl text-white/90">
            Artificial intelligence has moved well beyond chatbots and simple automation. The next frontier is AI agents, and they are already transforming the way forward-thinking businesses operate. If you have not yet heard of AI agents, now is the time to pay attention.
          </p>
          
          <p>
            At EsyAI, we build and deploy AI agents for businesses across the UK. In this guide, we break down exactly what AI agents are, how they work, and why they represent the biggest leap forward in business productivity in a generation.
          </p>

          <h2 className="text-2xl mt-12 mb-6">What Is an AI Agent?</h2>
          <p>
            An AI agent is a piece of software that can perceive its environment, make decisions, take actions, and learn from outcomes, all without continuous human input. Unlike traditional automation, which follows a fixed set of rules, an AI agent can reason through complex situations, adapt to new information, and pursue a goal across multiple steps and systems.
          </p>
          <p>
            Think of it like hiring a highly capable digital team member who never sleeps, never makes tired mistakes, and can handle dozens of tasks simultaneously.
          </p>

          <h2 className="text-2xl mt-12 mb-6">How Are AI Agents Different from Basic Automation?</h2>
          <p>
            Standard automation executes a predefined sequence. If A happens, do B. AI agents go much further. They can:
          </p>
          <ul className="list-disc pl-6 space-y-2 my-6 marker:text-purple-500">
            <li>Understand natural language instructions</li>
            <li>Browse the web, read documents and analyse data</li>
            <li>Make judgment calls based on context</li>
            <li>Coordinate with other agents and tools</li>
            <li>Learn and improve their performance over time</li>
          </ul>

          <h2 className="text-2xl mt-12 mb-6">Real-World Examples of AI Agents in Business</h2>
          <div className="space-y-6">
            <p><strong>Research Agent:</strong> Continuously monitors industry news, competitor activity, and market data. Summarises findings and delivers briefings to your team each morning, without anyone lifting a finger.</p>
            <p><strong>Sales Agent:</strong> Qualifies inbound leads, responds to enquiries, books discovery calls into your calendar, and updates your CRM, all automatically, around the clock.</p>
            <p><strong>Content Agent:</strong> Drafts blog posts, social media captions, and email campaigns based on your brand guidelines and content calendar, ready for a quick human review before publishing.</p>
            <p><strong>Support Agent:</strong> Handles customer queries, processes refund requests, checks order statuses, and escalates complex cases to a human, resolving the majority of tickets with no human involvement.</p>
            <p><strong>Operations Agent:</strong> Monitors your business systems, flags anomalies, generates reports, and triggers workflows when specific conditions are met.</p>
          </div>

          <h2 className="text-2xl mt-12 mb-6">Why AI Agents Are a Game-Changer for UK SMEs</h2>
          <p>
            For small and medium-sized businesses, AI agents are particularly powerful because they allow you to punch well above your weight. Tasks that would previously require a dedicated team member can now be handled autonomously, freeing your existing staff to focus on high-value, strategic work.
          </p>
          <ul className="list-disc pl-6 space-y-2 my-6 marker:text-purple-500">
            <li>A team of 5 can operate with the output capacity of a team of 15</li>
            <li>Agents work in parallel across multiple tasks simultaneously</li>
            <li>No training time, no sick days, no onboarding</li>
            <li>Costs a fraction of equivalent headcount</li>
          </ul>

          <h2 className="text-2xl mt-12 mb-6">Multi-Agent Systems: When Agents Work Together</h2>
          <p>
            The most advanced deployments involve multiple AI agents working in coordination, each specialised for a particular function, with an orchestrating agent managing priorities and passing tasks between them. This is the architecture EsyAI designs for ambitious clients who want to automate entire business functions end to end.
          </p>

          <h2 className="text-2xl mt-12 mb-6">Is My Business Ready for AI Agents?</h2>
          <p>
            If your business has repeatable processes that consume staff time, customer interactions that follow predictable patterns, or data that currently goes unanalysed because there is no capacity to review it, then you are ready for AI agents. EsyAI conducts a free discovery session to identify exactly where agents can deliver the fastest and highest-value impact in your specific operation.
          </p>

          <hr className="my-12 border-white/10" />

          <div className="not-prose mt-12 p-8 liquid-glass rounded-3xl border border-white/10 bg-gradient-to-br from-purple-900/20 to-transparent">
            <h3 className="text-xl font-heading text-white mb-3">Ready to deploy your first AI agent?</h3>
            <p className="text-white/70 mb-6 text-base font-body">
              We handle the full process, from identifying use cases to building and deploying your agents. Get in touch for a free discovery session.
            </p>
            <Link 
              to="/contact" 
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-black font-medium text-sm rounded-full hover:bg-white/90 transition-colors font-body"
            >
              Start the conversation <ArrowUpRight className="w-4 h-4 ml-2" />
            </Link>
          </div>

        </motion.article>
      </div>
    </div>
  );
}
